import React, { useCallback, useEffect } from 'react';
import { Box, JoyKitThemeProvider, TextV2, ButtonV2, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import type { useGetEventCTAButtonsDataLazyQuery } from '../CtaButtons/CtaButtons.graphql.generated';
import { EventRSVPAttendanceStatus } from '@graphql/generated-base';
import { useLogoutEventSessionMutation } from '@graphql/queries/eventSession.graphql.generated';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addError } from '@shared/utils/logger';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCountdown } from '@localization/hooks/useCountdown';
import { useGuestGuideTelemetry } from './GuestGuideTelemetry';
import { AddToCalendarAction } from './GuestGuideActions/AddToCalendarAction';
import { HotelDealsAction } from './GuestGuideActions/HotelDealsAction';
import { BrowseCouplesRegistryAction } from './GuestGuideActions/BrowseCouplesRegistryAction';
import { RemindMeToBookStayAction } from './GuestGuideActions/RemindMeToBookStayAction';
import { PriceAlertsAction } from './GuestGuideActions/PriceAlertsAction';
import { BookingCtaCard } from './GuestGuideActions/BookingCtaCard';
import { RsvpStatusSection } from './RsvpStatusSection';

type GuestGuideContentProps = Readonly<{
  eventDate?: number;
  sessionData: ReturnType<typeof useGetEventCTAButtonsDataLazyQuery>[1]['data'];
}>;

export const GuestGuideContent = ({ eventDate, sessionData }: GuestGuideContentProps) => {
  const { tq } = useTranslation('guestGuide');
  const telemetry = useGuestGuideTelemetry();
  const [logoutEvent, { loading: logoutLoading }] = useLogoutEventSessionMutation();
  const { eventInfo } = useEventInfo();
  const rsvpAttendanceStatus = sessionData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus;
  const isAttending = rsvpAttendanceStatus === EventRSVPAttendanceStatus.attending;

  const [isExperimentATreatment] = useIsFeatureEnabled('guestGuideHotelDealsPromotionExperiment');
  const [isExperimentBTreatment] = useIsFeatureEnabled('guestGuideBookingCtaExperiment');

  const needsCountdown = (isExperimentATreatment || isExperimentBTreatment) && !!eventDate;
  const { countDown } = useCountdown(needsCountdown ? eventDate : 0);
  const isEventFuture = needsCountdown && countDown.days > 0;
  const isEventDay = needsCountdown && countDown.days === 0;

  const showHotelDealsExperimentLabel = (isExperimentATreatment || isExperimentBTreatment) && (isEventFuture || isEventDay);
  const showHotelDealsSubtitle = isExperimentATreatment && !isExperimentBTreatment && (isEventFuture || isEventDay);
  const showBookingCtaCard = isExperimentBTreatment && (isEventFuture || isEventDay);
  const showBellIcon = isExperimentATreatment && !isExperimentBTreatment;

  const onLogoutHandle = useCallback(() => {
    if (!eventInfo?.eventId) {
      return;
    }

    logoutEvent({
      variables: { eventId: eventInfo.eventId },
      refetchQueries: () => ['GetEventSession', 'GetGuestSiteSchedule', 'GetEventCTAButtonsData'],
    })
      .then(() => {
        telemetry.clickLogout();
      })
      .catch(err => {
        addError(err, { context: 'GuestGuideContent', operation: 'logoutEvent' });
        telemetry.trackError('GuestGuideLogout', err);
      });
  }, [logoutEvent, eventInfo?.eventId, telemetry]);

  useEffect(() => {
    telemetry.guideShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <JoyKitThemeProvider>
      <Box
        backgroundColor={'white'}
        color={'inherit'}
        padding={6}
        minWidth={{ _: undefined, sm2: pxToRem(404) }}
      >
        <RsvpStatusSection sessionData={sessionData} />
        <Box
          typographyVariant={'hed4'}
          textAlign={['center', 'left']}
        >
          {tq`Guest guide to what's next.`}
        </Box>
        {showBookingCtaCard && (
          <Box marginTop={6}>
            <BookingCtaCard
              countdownDays={countDown.days}
              isEventDay={isEventDay}
            />
          </Box>
        )}
        <Box
          marginY={6}
          display={'grid'}
          rowGap={4}
        >
          <HotelDealsAction
            isAttending={isAttending}
            showExperimentLabel={showHotelDealsExperimentLabel}
            showBellIcon={showBellIcon}
            countdownDays={showHotelDealsSubtitle ? countDown.days : undefined}
            isEventDay={showHotelDealsSubtitle && isEventDay}
          />
          {isAttending && <AddToCalendarAction />}
          {isAttending && <RemindMeToBookStayAction />}
          {isAttending && <PriceAlertsAction />}
          <BrowseCouplesRegistryAction />
          <Flex
            alignItems="center"
            justifyContent="center"
            gap={3}
            marginTop={6}
          >
            <TextV2
              typographyVariant="label3"
              color="mono8"
            >
              {tq`Not You?`}
            </TextV2>

            <ButtonV2
              typographyVariant="label3"
              intent="neutral"
              color="mono8"
              variant="ghost"
              width="fit-content"
              height="fit-content"
              padding={0}
              loading={logoutLoading}
              onClick={onLogoutHandle}
              borderBottom={logoutLoading ? 'none' : '1px solid'}
              borderBottomColor={logoutLoading ? 'transparent' : 'mono5'}
              borderBottomLeftRadius="0"
              borderBottomRightRadius="0"
            >
              {tq`Sign Out`}
            </ButtonV2>
          </Flex>
        </Box>
      </Box>
    </JoyKitThemeProvider>
  );
};
